import type { SegmentDecision, VacationRequest, VacationRequestStatus, VacationSegment } from '../models/vacation.types';
import { latestRejectionTime } from '../../../../shared/vacations/segmentDecisions';

export interface SegmentWithPosition extends VacationSegment {
    position: number;
}

export type SegmentReviewState = 'pending' | 'approved' | 'rejected' | 'corrected';

const CORRECTION_IN_REVIEW_STATUSES: readonly VacationRequestStatus[] = [
    'PendingManager',
    'PendingClient',
    'PendingDP',
];

export function getSegmentTitle(position: number, total: number): string {
    return total > 1 ? `${position}º período` : 'Período de férias';
}

export function sortSegmentsByDate<T extends VacationSegment>(segments: T[]): Array<T & { position: number }> {
    return [...segments]
        .sort((a, b) => a.startDate.localeCompare(b.startDate))
        .map((seg, index) => ({
            ...seg,
            position: index + 1,
        }));
}

export function getSegmentPosition(segment: VacationSegment, segments: VacationSegment[]): number {
    const sorted = sortSegmentsByDate(segments);
    return sorted.find((s) => s.order === segment.order)?.position ?? segment.order;
}

export function resolveSegmentReviewState(
    decision: SegmentDecision | undefined,
    requestStatus?: VacationRequestStatus
): SegmentReviewState {
    if (!decision) return 'pending';
    if (decision.status === 'Approved') return 'approved';
    if (requestStatus && CORRECTION_IN_REVIEW_STATUSES.includes(requestStatus)) return 'corrected';
    return 'rejected';
}

export function resolveCorrectionReturnNote(
    request: Pick<VacationRequest, 'status' | 'lastCorrectionRejection' | 'dpSegmentDecisions' | 'clientSegmentDecisions'>
): string | null {
    const returned = request.lastCorrectionRejection;
    const returnedAt = returned?.at?.toMillis?.();
    if (request.status !== 'PendingCorrection' || !returnedAt) return null;

    return returnedAt > latestRejectionTime(request) ? returned?.note || 'Sem justificativa registrada.' : null;
}

export { resolveSegmentDecision, resolveSegmentDecisionSource } from '../../../../shared/vacations/segmentDecisions';
